/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useCarbServiceFeatures} from '@ion/applications/src/hooks/useCarbServiceFeatures';
import Features, {
  BLE,
  BMS,
  CARB,
  CARGO,
  DOOR,
  EA,
  ICN,
  IVT,
  LMS,
  MODBUS,
  SENTINEL,
  TELECRYPT,
  TPMS,
  WETS,
  WMS
} from '../security/Features';

/**
 * useAllowedServiceEnablements
 *
 * This hook generates an object of boolean flags indicating whether
 * each service-based feature is enabled in the current environment.
 *
 * It iterates over a predefined list of service identifiers (ICN, BLE,
 * IVT, CARGO, DOOR, TPMS, etc.) and uses `Features.hasFeature()` to
 * determine the enablement status of each feature.
 *
 * //Returns an object like:
 * {
 *   ICN: true,
 *   BLE: false,
 *   IVT: true,
 *   ...
 * }
 *
 * This is useful for conditional rendering, access control, or
 * feature-based UI logic throughout the application.
 */
export const useAllowedServiceEnablements = () => {
  const featureMap = {
    ICN,
    BLE,
    IVT,
    CARB,
    CARGO,
    DOOR,
    TPMS,
    WETS,
    BMS,
    LMS,
    WMS,
    EA,
    TELECRYPT,
    SENTINEL,
    MODBUS
  };

  const {hasRead, hasUpdate} = useCarbServiceFeatures(Features) || {hasRead: false, hasUpdate: false};

  return {
    ...Object.fromEntries(
      Object.entries(featureMap).map(([key, value]) => [
        key,
        key === 'CARB' ? Features.hasFeature(value) && hasUpdate && hasRead : Features.hasFeature(value)
      ])
    )
  };
};
export default useAllowedServiceEnablements;
